<script setup lang="ts">
// https://reka-ui.com/docs/components/dropdown-menu#item
import type { Locale } from 'vue-i18n';
import { normalizeLanguage, languages } from '@@/shared/application';
import { getUrlParamsFromCriteria, getCriteriaFromQuery } from '@@/shared/property/url-serp';

const LOCALE_TO_COUNTRY: Record<Locale, string> = {
  en: `English`,
  fr: `Français`,
} as const;
const { locale, setLocale } = useI18n();
const selectLocales = computed<Array<Locale>>(() => {
  return languages.filter(availableLocale => availableLocale !== locale.value);
});

const localeRoute = useLocaleRoute();
const route = useRoute();
const routeNameWithoutCountry = computed(() => {
  if (typeof route.name !== 'string') return ``;
  return route.name.split(`___`)?.[0] ?? ``;
});

const ROUTE_HAVING_TRANSLATED_PARAMS = [
  `serp-serpSlug`,
  `property-propertySlug-rpropertyListingRef`,
];
const router = useRouter();
const switchLocalePath = useSwitchLocalePath();
const isOpened = ref(false);
function onLocaleChange(newLocale: Locale) {
  setLocale(newLocale);
  const shouldTranslateParams = ROUTE_HAVING_TRANSLATED_PARAMS.includes(routeNameWithoutCountry.value);
  if (!shouldTranslateParams) return switchLocalePath(newLocale);
  if (routeNameWithoutCountry.value === `serp-serpSlug`) return onSerpLocaleChange(newLocale);
  if (routeNameWithoutCountry.value === `property-propertySlug-rpropertyListingRef`) return onPropertyLocaleChange(newLocale);
  switchLocalePath(newLocale);
  isOpened.value = false;
}

// We don't have access to the property nor the head data
// ⇒ since select is only available on client side, we can safely query the DOM
function onPropertyLocaleChange(newLocale: Locale) {
  const hrefLangUrl = document.querySelector(`#i18n-alt-${newLocale}`)?.getAttribute(`href`);
  if (!hrefLangUrl) return switchLocalePath(newLocale);
  const langUrl = new URL(hrefLangUrl);
  router.push(langUrl.pathname);
}

function onSerpLocaleChange(newLocale: Locale) {
  const translatedRoute = localeRoute(`serp-serpSlug`, newLocale);
  if (!translatedRoute || !translatedRoute.name) return switchLocalePath(newLocale);
  const propertySearchCriteria = getCriteriaFromQuery({ ...route.query, serpSlug: route.params.serpSlug });
  const normalizedUrlQueryParams = getUrlParamsFromCriteria(normalizeLanguage(newLocale), propertySearchCriteria);
  router.push({
    name: translatedRoute.name,
    params: { serpSlug: normalizedUrlQueryParams.serpSlug },
    query: normalizedUrlQueryParams.query,
  });
}

// CSS var --reka-select-trigger-width isn't computed as we want
const dropdownMenuTriggerRef = ref<HTMLElement | null>(null);
const { width } = useElementBounding(dropdownMenuTriggerRef);
</script>

<template>
  <DropdownMenuRoot
    v-model:open="isOpened"
    class="relative isolate"
  >
    <DropdownMenuTrigger
      ref="dropdownMenuTriggerRef"
      class="w-full lg:w-24 group-[.is-prestige]:lg:text-xs border-t border-black/10 pt-l lg:border-none lg:pt-0"
    >
      <div
        v-if="LOCALE_TO_COUNTRY[locale]"
        class="flex w-full justify-between items-center gap-2xs font-semibold group-[.is-prestige]:font-normal group-[.is-prestige]:uppercase group-[.is-prestige]:tracking-wider"
      >
        <span v-text="LOCALE_TO_COUNTRY[locale]" />
        <Icon
          name="iad-icon:caret-down"
          class="transition origin-center size-4"
          :class="{ 'rotate-180': isOpened }"
        />
      </div>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      class="max-h-60 overflow-auto overscroll-contain
        rounded-md bg-white ring-1 ring-neutral-60 shadow-popover
        divide-y divide-neutral-80
        overflow-hidden
        will-change-[transform,opacity] data-[state=open]:animate-slideDownAndFade empty:hidden
        group-[.is-prestige]:uppercase group-[.is-prestige]:lg:text-xs group-[.is-prestige]:tracking-wider
        z-20"
      position="popper"
      position-strategy="absolute"
      :side-offset="5"
      :style="{ width: `${width}px` }"
    >
      <DropdownMenuItem
        v-for="availableLocale in selectLocales"
        :key="availableLocale"
        @select="onLocaleChange(availableLocale)"
      >
        <div
          v-if="LOCALE_TO_COUNTRY[availableLocale]"
          class="flex gap-s py-s px-m items-center cursor-pointer"
        >
          <span v-text="LOCALE_TO_COUNTRY[availableLocale]" />
        </div>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenuRoot>
</template>
